import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const LANGUAGE_COLUMN_CODES = {
  arabic: 'ar', bengali: 'bn', chinese: 'zh', czech: 'cs', danish: 'da',
  dutch: 'nl', english: 'en', filipino: 'fil', finnish: 'fi', french: 'fr',
  german: 'de', greek: 'el', gujarati: 'gu', hebrew: 'he', hindi: 'hi',
  hungarian: 'hu', indonesian: 'id', italian: 'it', japanese: 'ja',
  kannada: 'kn', katakana: 'ja', korean: 'ko', malay: 'ms', malayalam: 'ml',
  marathi: 'mr', norwegian: 'no', odia: 'or', persian: 'fa', polish: 'pl',
  portuguese: 'pt', punjabi: 'pa', romanian: 'ro', russian: 'ru',
  spanish: 'es', swahili: 'sw', swedish: 'sv', tamil: 'ta', telugu: 'te',
  thai: 'th', turkish: 'tr', urdu: 'ur', vietnamese: 'vi',
};

function languageCodeForColumn(column) {
  const normalized = String(column)
    .trim()
    .toLowerCase()
    .replace(/\s+(sound|pronunciation)$/, '');
  return LANGUAGE_COLUMN_CODES[normalized] || null;
}

function cellsForRow(row) {
  if (Array.isArray(row)) return row;
  return Array.isArray(row?.cells) ? row.cells : [];
}

function filterForLanguagePair(data, nativeCode, targetCode, nativeLabel, targetLabel) {
  if (!data?.sets || !nativeCode || !targetCode) return data;
  const comparisonSets = [];
  const otherSets = [];

  data.sets.forEach((set) => {
    const languageCodes = set.columns.map(languageCodeForColumn);
    const isComparisonSet = /^Set\s+/i.test(set.title || '')
      || languageCodes.filter(Boolean).length >= 2;
    const keptIndexes = set.columns
      .map((_, index) => index)
      .filter((index) => index < 2
        || !languageCodes[index]
        || languageCodes[index] === nativeCode
        || languageCodes[index] === targetCode);
    const filteredSet = {
      ...set,
      title: isComparisonSet
        ? `${targetLabel || targetCode.toUpperCase()} · ${nativeLabel || nativeCode.toUpperCase()}`
        : set.title,
      columns: keptIndexes.map((index) => set.columns[index]),
      rows: set.rows.map((row) => {
        const cells = cellsForRow(row);
        return keptIndexes
          .filter((index) => index < cells.length)
          .map((index) => cells[index]);
      }),
      hasNativeColumn: languageCodes.includes(nativeCode),
    };
    if (isComparisonSet) comparisonSets.push(filteredSet);
    else otherSets.push(filteredSet);
  });

  const selectedComparison = comparisonSets.find((set) => set.hasNativeColumn)
    || comparisonSets[0];
  const cleanSet = ({ hasNativeColumn, ...set }) => set;
  return {
    ...data,
    title: `${targetLabel || targetCode.toUpperCase()} Alphabet`,
    subtitle: `Each letter with ${nativeLabel || nativeCode.toUpperCase()} pronunciation`,
    sets: [
      ...(selectedComparison ? [cleanSet(selectedComparison)] : []),
      ...otherSets.map(cleanSet),
    ],
  };
}

function PronunciationTable({ title, columns, rows, targetDir }) {
  return (
    <section className="mb-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 bg-gradient-to-r from-indigo-600 to-violet-500 px-4 py-3 sm:px-5 sm:py-4">
        <h2 className="min-w-0 flex-1 text-sm font-semibold leading-relaxed text-white sm:text-base">
          {title}
        </h2>
        <span className="shrink-0 rounded-full border border-white/25 bg-white/15 px-2.5 py-1 text-xs font-semibold text-white">
          {rows.length}
        </span>
      </div>

      <div className="sm:hidden">
        {rows.map((row, ri) => {
          const cells = cellsForRow(row);
          return (
            <article
              key={row?.id || ri}
              className={`flex items-start gap-3.5 border-b border-slate-200 px-3.5 py-3.5 last:border-b-0 ${
                ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'
              }`}
            >
              <div
                dir={targetDir}
                className="flex min-h-[58px] w-[72px] shrink-0 items-center justify-center rounded-xl border border-violet-200 bg-violet-50 px-2 py-2.5 text-center text-2xl font-medium leading-relaxed text-violet-800"
              >
                {cells[0]}
              </div>
              <dl className="min-w-0 flex-1 space-y-2.5">
                {columns.slice(1).map((column, ci) => (
                  <div key={`${column}-${ci}`}>
                    <dt className="mb-0.5 text-[10px] font-bold uppercase leading-tight tracking-wider text-slate-500">
                      {column}
                    </dt>
                    <dd
                      className={`break-words leading-relaxed ${
                        ci === 0
                          ? 'text-[15px] font-medium text-slate-900'
                          : 'text-[13px] font-normal text-slate-700'
                      }`}
                    >
                      {cells[ci + 1] ?? ''}
                    </dd>
                  </div>
                ))}
              </dl>
            </article>
          );
        })}
      </div>

      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-indigo-50 border-b border-slate-200">
              {columns.map((col) => (
                <th
                  key={col}
                  className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-indigo-700"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => {
              const cells = cellsForRow(row);
              return (
              <tr
                key={row?.id || ri}
                className={`border-b border-slate-100 transition-colors hover:bg-indigo-50/60 ${
                  ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'
                }`}
              >
                {cells.map((cell, ci) => (
                  <td
                    key={ci}
                    dir={ci === 0 ? targetDir : undefined}
                    className={`px-4 py-3 align-middle ${
                      ci === 0
                        ? 'w-20 text-center text-2xl font-medium leading-relaxed text-violet-800'
                        : ci === 1
                        ? 'min-w-[110px] text-sm font-medium text-slate-900'
                        : 'min-w-[110px] text-sm font-normal leading-relaxed text-slate-700'
                    }`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function AlphabetTables({
  data,
  compact = false,
  nativeCode,
  targetCode,
  nativeLabel,
  targetLabel,
  nativeDir = 'ltr',
  targetDir = 'ltr',
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  if (!data) return null;
  const filteredData = filterForLanguagePair(
    data,
    nativeCode,
    targetCode,
    nativeLabel,
    targetLabel,
  );
  const pairData = {
    ...filteredData,
    title: t('learn.alphabetTitle', { lang: targetLabel || targetCode?.toUpperCase() }),
    subtitle: t('learn.alphabetWithNative', { native: nativeLabel || nativeCode?.toUpperCase() }),
  };
  const visibleSets = expanded
    ? pairData.sets
    : pairData.sets.slice(0, 1).map((set) => ({ ...set, rows: set.rows.slice(0, 12) }));
  return (
    <div className="p-4 md:p-6 bg-slate-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-slate-800 leading-tight">{pairData.title}</h1>
        {pairData.subtitle && (
          <p className="mt-1 text-slate-500 text-sm">{pairData.subtitle}</p>
        )}
      </div>
      {pairData.localizedIntroduction && (
        <section dir={nativeDir} className="mb-6 overflow-hidden rounded-2xl border border-indigo-200 bg-white shadow-sm">
          <div className="border-b border-indigo-100 bg-gradient-to-r from-indigo-50 to-violet-50 px-5 py-4">
            <p className="text-xs font-semibold uppercase leading-relaxed tracking-widest text-indigo-600">{pairData.localizedIntroduction.title}</p>
          </div>
          <div className="space-y-3 px-5 py-5 text-sm leading-relaxed text-slate-700">
            <p>{pairData.localizedIntroduction.overview}</p>
            <p>{pairData.localizedIntroduction.countSummary}</p>
            <p>{pairData.localizedIntroduction.specialSymbolsSummary}</p>
            <p className="rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 font-semibold text-violet-900">
              {pairData.localizedIntroduction.comparison}
            </p>
          </div>
        </section>
      )}
      {compact && (
        <div className="mb-5 flex items-center justify-between gap-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
          <div><p className="text-sm font-bold text-indigo-900">{t('learn.alphabetGuideDesc')}</p><p className="text-xs text-indigo-700">{t('learn.alphabetEach')}</p></div>
          <button onClick={() => setExpanded((value) => !value)} className="shrink-0 rounded-xl bg-indigo-600 px-3 py-2 text-xs font-bold text-white">{expanded ? t('common.done') : t('nav.alphabets')}</button>
        </div>
      )}
      {visibleSets.map((set) => (
        <PronunciationTable
          key={set.title}
          title={set.title}
          columns={set.columns}
          rows={set.rows}
          targetDir={targetDir}
        />
      ))}
    </div>
  );
}
